import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { Container, Row, Col, Card, Alert, Spinner, Form, Button, Table, Badge } from 'react-bootstrap';
import { RefreshCw, TrendingUp, DollarSign, Building, Users, Calendar, Briefcase } from 'lucide-react';
import Plot from 'react-plotly.js';
import { apiClient } from '../services/api';

const JobsAnalytics = () => {
  const [stats, setStats] = useState(null);
  const [dailyStats, setDailyStats] = useState([]);
  const [charts, setCharts] = useState({});
  const [recentJobs, setRecentJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Filters
  const [filters, setFilters] = useState({
    days: 30,
    company: '',
    jobFunction: '',
    search: '',
    queryTerm: 'sales'
  });

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const [
        statsResponse,
        dailyStatsResponse,
        chartsResponse,
        jobsResponse
      ] = await Promise.all([
        apiClient.get('/jobs/stats', {
          params: {
            days: filters.days,
            company: filters.company || undefined,
            job_function: filters.jobFunction || undefined
          }
        }),
        apiClient.get('/jobs/daily-stats', {
          params: {
            days: filters.days,
            query_term: filters.queryTerm
          }
        }),
        apiClient.get('/jobs/charts', {
          params: { days: filters.days }
        }),
        apiClient.get('/jobs', {
          params: {
            limit: 20,
            company: filters.company || undefined,
            job_function: filters.jobFunction || undefined,
            search: filters.search || undefined
          }
        })
      ]);

      setStats(statsResponse.data);
      setDailyStats(dailyStatsResponse.data);
      setCharts(chartsResponse.data);
      setRecentJobs(jobsResponse.data);
    } catch (err) {
      setError('Failed to fetch job analytics data');
      console.error(err);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    fetchData();
  }, [fetchData]);

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({ ...prev, [field]: value }));
  };

  const formatSalary = (amount) => {
    if (!amount) return 'N/A';
    return `$${amount.toLocaleString()}`;
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    return new Date(dateString).toLocaleDateString();
  };

  const salaryChart = useMemo(() => {
    if (!charts.salary_chart) return null;
    try {
      return JSON.parse(charts.salary_chart);
    } catch (error) {
      console.error('Failed to parse salary chart', error);
      return null;
    }
  }, [charts.salary_chart]);

  const postingChart = useMemo(() => {
    if (!charts.posting_chart) return null;
    try {
      return JSON.parse(charts.posting_chart);
    } catch (error) {
      console.error('Failed to parse posting chart', error);
      return null;
    }
  }, [charts.posting_chart]);

  if (loading) {
    return (
      <Container className="mt-4">
        <div className="text-center">
          <Spinner animation="border" role="status">
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-2">Loading job analytics...</p>
        </div>
      </Container>
    );
  }

  if (error) {
    return (
      <Container className="mt-4">
        <Alert variant="danger">
          <Alert.Heading>Error Loading Data</Alert.Heading>
          <p>{error}</p>
          <Button variant="outline-danger" onClick={fetchData}>
            <RefreshCw size={16} className="me-1" />
            Try Again
          </Button>
        </Alert>
      </Container>
    );
  }

  return (
    <Container className="mt-4">
      <Row className="mb-4">
        <Col>
          <h1 className="d-flex align-items-center">
            <TrendingUp className="me-2" />
            Job Market Analytics
          </h1>
          <p className="text-muted">
            Track Singapore job postings, salary trends, and market demand from MyCareersFuture data.
          </p>
        </Col>
      </Row>

      {/* Filters */}
      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Body>
              <h5>Filters</h5>
              <Row>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Time Period</Form.Label>
                    <Form.Select
                      value={filters.days}
                      onChange={(e) => handleFilterChange('days', parseInt(e.target.value))}
                    >
                      <option value={7}>Last 7 days</option>
                      <option value={30}>Last 30 days</option>
                      <option value={90}>Last 90 days</option>
                      <option value={180}>Last 6 months</option>
                      <option value={365}>Last year</option>
                    </Form.Select>
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Company</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Filter by company..."
                      value={filters.company}
                      onChange={(e) => handleFilterChange('company', e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Job Function</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Filter by job function..."
                      value={filters.jobFunction}
                      onChange={(e) => handleFilterChange('jobFunction', e.target.value)}
                    />
                  </Form.Group>
                </Col>
                <Col md={3}>
                  <Form.Group>
                    <Form.Label>Query Term</Form.Label>
                    <Form.Control
                      type="text"
                      value={filters.queryTerm}
                      onChange={(e) => handleFilterChange('queryTerm', e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
              <Row className="mt-3">
                <Col md={6}>
                  <Form.Group>
                    <Form.Label>Keyword Search</Form.Label>
                    <Form.Control
                      type="text"
                      placeholder="Search titles or skills..."
                      value={filters.search}
                      onChange={(e) => handleFilterChange('search', e.target.value)}
                    />
                  </Form.Group>
                </Col>
              </Row>
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Summary Cards */}
      {stats && (
        <Row className="mb-4">
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <Users className="text-primary mb-2" size={32} />
                <h3>{stats.total_jobs.toLocaleString()}</h3>
                <p className="text-muted mb-0">Total Jobs</p>
                <small className="text-muted">{stats.date_range}</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <DollarSign className="text-success mb-2" size={32} />
                <h3>{formatSalary(stats.avg_salary_min)}</h3>
                <p className="text-muted mb-0">Avg Min Salary</p>
                <small className="text-muted">Median: {formatSalary(stats.median_salary_min)}</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <DollarSign className="text-warning mb-2" size={32} />
                <h3>{formatSalary(stats.avg_salary_max)}</h3>
                <p className="text-muted mb-0">Avg Max Salary</p>
                <small className="text-muted">Median: {formatSalary(stats.median_salary_max)}</small>
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card className="text-center">
              <Card.Body>
                <TrendingUp className="text-info mb-2" size={32} />
                <h3>{formatSalary(stats.min_salary)} - {formatSalary(stats.max_salary)}</h3>
                <p className="text-muted mb-0">Salary Range</p>
                <small className="text-muted">Min - Max</small>
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      {/* Charts */}
      <Row className="mb-4">
        {salaryChart && (
          <Col md={6}>
            <Card>
              <Card.Body>
                <Plot
                  data={salaryChart.data}
                  layout={salaryChart.layout}
                  config={{ responsive: true }}
                  style={{ width: '100%', height: '400px' }}
                />
              </Card.Body>
            </Card>
          </Col>
        )}
        {postingChart && (
          <Col md={6}>
            <Card>
              <Card.Body>
                <Plot
                  data={postingChart.data}
                  layout={postingChart.layout}
                  config={{ responsive: true }}
                  style={{ width: '100%', height: '400px' }}
                />
              </Card.Body>
            </Card>
          </Col>
        )}
      </Row>

      {/* Top Companies and Industries */}
      {stats && (
        <Row className="mb-4">
          <Col md={6}>
            <Card>
              <Card.Header>
                <h5><Building className="me-2" size={20} />Top Companies</h5>
              </Card.Header>
              <Card.Body>
                {stats.top_companies.length > 0 ? (
                  <div>
                    {stats.top_companies.map((company, index) => (
                      <div key={index} className="d-flex justify-content-between align-items-center mb-2">
                        <span>{company.name}</span>
                        <Badge bg="primary">{company.count} jobs</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted">No company data available</p>
                )}
              </Card.Body>
            </Card>
          </Col>
          <Col md={6}>
            <Card>
              <Card.Header>
                <h5><TrendingUp className="me-2" size={20} />Top Job Functions</h5>
              </Card.Header>
              <Card.Body>
                {stats.top_job_functions.length > 0 ? (
                  <div>
                    {stats.top_job_functions.map((jobFunction, index) => (
                      <div key={index} className="d-flex justify-content-between align-items-center mb-2">
                        <span>{jobFunction.name}</span>
                        <Badge bg="secondary">{jobFunction.count} jobs</Badge>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-muted">No job function data available</p>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>
      )}

      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Header>
              <h5><Briefcase className="me-2" size={20} />Daily Market Snapshot</h5>
            </Card.Header>
            <Card.Body>
              {dailyStats.length > 0 ? (
                <Table responsive hover>
                  <thead>
                    <tr>
                      <th>Date</th>
                      <th>Query</th>
                      <th>Total Listings</th>
                      <th>New Listings</th>
                      <th>Median Salary Range</th>
                    </tr>
                  </thead>
                  <tbody>
                    {dailyStats.slice(0, 10).map((row) => (
                      <tr key={`${row.date}-${row.query_term}`}>
                        <td>{formatDate(row.date)}</td>
                        <td>{row.query_term}</td>
                        <td>{row.total_listings}</td>
                        <td>{row.new_listings}</td>
                        <td>{formatSalary(row.median_salary_min)} - {formatSalary(row.median_salary_max)}</td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <p className="text-muted mb-0">No daily market snapshot data available for this query term.</p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Recent Jobs Table */}
      <Row className="mb-4">
        <Col>
          <Card>
            <Card.Header>
              <h5><Calendar className="me-2" size={20} />Recent Job Postings</h5>
            </Card.Header>
            <Card.Body>
              {recentJobs.length > 0 ? (
                <Table responsive hover>
                  <thead>
                    <tr>
                      <th>Title</th>
                      <th>Company</th>
                      <th>Job Function</th>
                      <th>Salary Range</th>
                      <th>Posted Date</th>
                      <th>Seniority</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recentJobs.map((job) => (
                      <tr key={job.id}>
                        <td>
                          <strong>{job.title}</strong>
                          {job.url && (
                            <>
                              <br />
                              <small>
                                <a href={job.url} target="_blank" rel="noopener noreferrer">
                                  View Job
                                </a>
                              </small>
                            </>
                          )}
                        </td>
                        <td>{job.company || 'N/A'}</td>
                        <td>{job.job_function || 'N/A'}</td>
                        <td>
                          {job.salary_min || job.salary_max ? (
                            <>
                              {formatSalary(job.salary_min)} - {formatSalary(job.salary_max)}
                            </>
                          ) : (
                            'Not specified'
                          )}
                        </td>
                        <td>{formatDate(job.posting_date)}</td>
                        <td>
                          {job.seniority && (
                            <Badge bg="info">{job.seniority}</Badge>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </Table>
              ) : (
                <p className="text-muted">No recent jobs found</p>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>

      {/* Refresh Button */}
      <Row className="mb-4">
        <Col className="text-center">
          <Button variant="primary" onClick={fetchData} disabled={loading}>
            <RefreshCw size={16} className="me-1" />
            Refresh Data
          </Button>
        </Col>
      </Row>
    </Container>
  );
};

export default JobsAnalytics;
